import { createAsyncThunk, createSelector, createSlice, type PayloadAction } from '@reduxjs/toolkit'
import { z } from 'zod'
import { logWarning } from '@/services/errorService'
import { getReferral, touchReferral } from '@/lib/referralStorage'

import type {
	LocationData,
	MenuSchedule,
	OrderType,
	Tender,
	TipSettings,
} from '@/shared/api/location'
import {
	calculateCart,
	type CalculateCartItemModifier,
	type CalculateCartRequest,
} from '@/shared/api/cart'
import { getGiftCardBalance } from '@/shared/api/giftCard'
import { ApiError, CartApiError } from '@/lib/apiErrors'
import {
	CartItemSchema,
	type CartItem,
	type CartItemModifier,
	type CalculationDomainError,
	type CalculationResponse,
} from '@/shared/types/cart'
import type { AppDispatch, RootState } from '@/store'

// Validates raw localStorage items against the CartItemSchema — filters out any
// malformed entries (stale data, manual edits) rather than crashing or loading corrupt state.
function parseStoredItems(raw: unknown): CartItem[] {
	if (!Array.isArray(raw)) return []
	return raw.flatMap((entry, index) => {
		const result = CartItemSchema.safeParse(entry)
		if (!result.success) {
			logWarning('[ticketSlice] Skipping malformed cart item from localStorage', { index })
			return []
		}
		return [result.data]
	})
}

const DeliveryAddressSchema = z.object({
	addressLine1: z.string().trim().min(1),
	addressLine2: z.string().optional(),
	city: z.string().trim().min(1),
	state: z.string().optional(),
	zip: z.string().trim().min(1),
})

// Validates the stored deliveryAddress before it enters state — same defence as
// parseStoredItems. Returns null when absent or malformed (tampered/legacy data) so a
// truthy scalar or partial object can never reach the order payload after reload.
function parseStoredDeliveryAddress(raw: unknown): DeliveryAddress | null {
	if (raw === undefined || raw === null) return null
	const result = DeliveryAddressSchema.safeParse(raw)
	if (!result.success) {
		logWarning('[ticketSlice] Skipping malformed deliveryAddress from localStorage')
		return null
	}
	return result.data
}

const LOCAL_STORAGE_TTL_MS = 7_200_000 // 2 hours — matches Ember set-local-storage.js
const TIP_PERCENTAGE_DIVISOR = 10_000 // Ember raw format: predefinedTipPercentage stored as integer × 10000

// ---------------------------------------------------------------------------
// Local storage shape
// ---------------------------------------------------------------------------

interface StoredTicketData {
	items?: CartItem[]
	orderTypeGuid?: string
	menuScheduleRef?: string
	dineInTableName?: string
	isDeferred?: boolean
	expectedTimeUtc?: string
	orderTimeUtc?: string
	customerSelectedDay?: string
	dropOffPointRef?: string | null
	dropOffPointLastMileValue?: string | null
	deliveryAddress?: DeliveryAddress | null
	deliveryInstructions?: string | null
	isContactlessDelivery?: boolean
	discountCode?: string | null
	expiresAt?: number
}

export interface DeliveryAddress {
	addressLine1: string
	addressLine2?: string
	city: string
	state?: string
	zip: string
}

export interface TicketState {
	// --- Order settings (STO-3073) ---
	currentRef: string | null
	selectedOrderTypeGuid: string | null
	selectedOrderType: OrderType | null
	selectedMenuSchedule: MenuSchedule | null
	dineInTableName: string | null
	isDeferred: boolean
	expectedTimeUtc: string | null
	orderTimeUtc: string | null
	customerSelectedDay: string | null
	dropOffPointRef: string | null
	dropOffPointLastMileValue: string | null
	deliveryAddress: DeliveryAddress | null
	// STO-3352: collected at checkout when ew-olo-checkout-address-collection is ON
	deliveryInstructions: string | null
	isContactlessDelivery: boolean
	// STO-3352 AC10: ETA ISO string returned by validate-distance; used to filter time slots in Order Settings
	deliveryEtaUtc: string | null
	referralGuid: string | null
	referralSource: string | null
	rwgToken: string | null
	pendingReorder: { itemGuid: string; itemRef: string } | null
	storeSettingsChangedError: string | null

	// --- Cart items (STO-3130) ---
	// Guards persistToLocalStorage — only true after restoreCart completes.
	// Prevents setOrderType dispatches from StartOrder (which fire before restoreCart)
	// from overwriting a valid stored cart with an empty items array.
	cartRestored: boolean
	items: CartItem[]
	isCalculating: boolean
	calculations: CalculationResponse | null
	calculateErrors: CalculationDomainError[] | null
	// Notification signal: UI reads this, shows a toast, then dispatches clearItemsRemovedCount.
	// Do not auto-clear with setTimeout inside the thunk — the UI component owns the timer.
	itemsRemovedCount: number
	// Refs of items flagged ITMOOSTK by the calculate API. UI navigates to sold-out warning page when non-empty.
	itemsOutOfStock: string[]
	// Ember parity: dineInAllowDuringOffHours from LocationData.
	// When false, dine-in checkout is blocked while duringOffHours is true on the selected schedule.
	dineInAllowDuringOffHours: boolean
	// Count of non-dine-in order types available at this location.
	// Edit button is suppressed when there is only one non-dine-in order type with no scheduling allowed.
	nonDineInOrderTypeCount: number
	// True when the TAKEOUT order type has at least one future time slot available.
	// null means unknown — the time-slots fetch failed or has not completed. Consumers must test
	// === false, never falsiness: null is not a confirmed absence of future slots.
	hasFutureSlotsForTakeout: boolean | null
	// True when the DELIVERY order type has at least one future time slot available.
	// Same null-means-unknown rule as hasFutureSlotsForTakeout above.
	hasFutureSlotsForDelivery: boolean | null

	// True after checkout is blocked for an order-type minimum — drives warning/success alert visibility.
	// Reset when order type changes or when the user drops back below the minimum.
	showOrderTypeMinWarning: boolean
	// Bumped each time checkout is retried while the warning is already showing — triggers nudge animation.
	orderTypeMinAlertNudge: number

	// --- Tip state ---
	// STO-3629: tipSettings is the static location configuration sent on every calculate() call.
	// Ember: routes/location.js:137 — ticket.tipSettings = model.get('tipSettings')
	tipSettings: TipSettings | null
	// STO-3130 — user's runtime tip selection
	selectedTip: 'percent' | 'custom' | null
	tipPercentage: number | null
	tipCustomAmount: number | null

	// --- Loyalty discount (STO-3353) ---
	// Owned by ticketSlice (not loyaltySlice) because it affects the calculate request.
	// R3: always dispatch clearLoyaltyDiscount + resetLoyalty + calculate() together on reset.
	loyaltyDiscount: { discountRef: string; source: string } | null
	// discountRef of the reward that failed to apply on the last calculate attempt.
	// Cleared when Redeem or Unredeem is clicked, or when loyalty state is reset.
	loyaltyDiscountError: string | null

	// --- S5-04: Coupon code ---
	discountCode: string | null
	discountName: string | null
	discountCodeError: string | null
	// The invalid code captured at ORDCOUPINV time — null otherwise.
	// Kept separate so the UI can interpolate it into the error message.
	discountCodeErrorCode: string | null
	discountCodeValid: boolean
	discountCodeLoading: boolean

	// --- S5-04: Gift card ---
	giftCardNumber: string | null
	giftCardBalance: number | null // cents
	giftCardAppliedAmount: number | null // cents
	giftCardErrors: string | null

	// --- S5-04: Payment tender ---
	tenderTypeGuid: string | null
	/** False when the selected tender allows tips; true when it does not (e.g. cash). Ember: selectedTender.isTipAllowed. */
	isTipDisabledForSelectedTender: boolean
	// STO-3720: location tenders synced from useLocationBootstrap (same pattern as tipSettings).
	// Populated before the calculate thunk can run — initial [] prevents coercion until bootstrap runs.
	tenders: Tender[]

	// --- STO-3425: DSP delivery ETA ---
	// Minutes from now until the DSP-quoted delivery ETA, computed from validate-distance response.eta.
	// Not persisted to localStorage — recomputed fresh on each address validation call.
	distanceLeadTimeInMinutes: number | null

	// --- STO-3489: DSP preferred quote ID ---
	// Delivery quote reference returned by validate-distance. Must be sent in the order submission.
	preferredQuoteId: string | null

	// --- STO-3691: Checkout contact fields ---
	// The values the customer typed into the Customer Information section. Held here rather than
	// in ContactInformation's local state so they survive the component unmounting on a
	// checkout → menu → checkout round trip.
	// Deliberately NOT persisted to localStorage: these are PII and the 2-hour TTL blob is shared
	// with the cart, so a later customer on the same device would inherit them.
	// Not a substitute for auth.user — that still holds the saved server profile. On mount the
	// component prefers this (the customer's in-session edit) and falls back to the profile.
	contactInfo: {
		phone: string | null
		email: string | null
		firstName: string | null
		lastName: string | null
	}
}

const initialState: TicketState = {
	currentRef: null,
	selectedOrderTypeGuid: null,
	selectedOrderType: null,
	selectedMenuSchedule: null,
	dineInTableName: null,
	isDeferred: false,
	expectedTimeUtc: null,
	orderTimeUtc: null,
	customerSelectedDay: null,
	dropOffPointRef: null,
	dropOffPointLastMileValue: null,
	deliveryAddress: null,
	deliveryInstructions: null,
	isContactlessDelivery: false,
	deliveryEtaUtc: null,
	referralGuid: null,
	referralSource: null,
	rwgToken: null,
	pendingReorder: null,
	storeSettingsChangedError: null,
	cartRestored: false,
	items: [],
	isCalculating: false,
	calculations: null,
	calculateErrors: null,
	itemsRemovedCount: 0,
	itemsOutOfStock: [],
	dineInAllowDuringOffHours: false,
	nonDineInOrderTypeCount: 0,
	hasFutureSlotsForTakeout: null,
	hasFutureSlotsForDelivery: null,
	showOrderTypeMinWarning: false,
	orderTypeMinAlertNudge: 0,
	tipSettings: null,
	selectedTip: null,
	tipPercentage: null,
	tipCustomAmount: null,
	loyaltyDiscount: null,
	loyaltyDiscountError: null,
	discountCode: null,
	discountName: null,
	discountCodeError: null,
	discountCodeErrorCode: null,
	discountCodeValid: false,
	discountCodeLoading: false,
	giftCardNumber: null,
	giftCardBalance: null,
	giftCardAppliedAmount: null,
	giftCardErrors: null,
	tenderTypeGuid: null,
	isTipDisabledForSelectedTender: false,
	tenders: [],
	distanceLeadTimeInMinutes: null,
	preferredQuoteId: null,
	contactInfo: {
		phone: null,
		email: null,
		firstName: null,
		lastName: null,
	},
}

// ---------------------------------------------------------------------------
// localStorage persistence (order settings only)
// ---------------------------------------------------------------------------

function persistToLocalStorage(state: TicketState): void {
	if (!state.currentRef) return
	// Do not write until restoreCart has completed — any dispatch before restore
	// would overwrite the stored entry and lose items that haven't loaded yet.
	if (!state.cartRestored) return
	const data: StoredTicketData = {}
	if (state.selectedOrderTypeGuid) data.orderTypeGuid = state.selectedOrderTypeGuid
	if (state.selectedMenuSchedule) data.menuScheduleRef = state.selectedMenuSchedule.ref
	if (state.dineInTableName) data.dineInTableName = state.dineInTableName
	data.isDeferred = state.isDeferred
	if (state.expectedTimeUtc) data.expectedTimeUtc = state.expectedTimeUtc
	if (state.orderTimeUtc) data.orderTimeUtc = state.orderTimeUtc
	if (state.customerSelectedDay) data.customerSelectedDay = state.customerSelectedDay
	if (state.dropOffPointRef) data.dropOffPointRef = state.dropOffPointRef
	if (state.dropOffPointLastMileValue)
		data.dropOffPointLastMileValue = state.dropOffPointLastMileValue
	if (state.deliveryAddress) data.deliveryAddress = state.deliveryAddress
	if (state.deliveryInstructions) data.deliveryInstructions = state.deliveryInstructions
	if (state.isContactlessDelivery) data.isContactlessDelivery = state.isContactlessDelivery
	if (state.discountCode) data.discountCode = state.discountCode
	// Only persist items when the cart is non-empty. An empty cart (clearCart, ITMNA
	// recovery) writes no items field — restoreCart defaults to [] when absent.
	// cartRestored is already verified above, so this never races with bootstrap.
	if (state.items.length > 0) {
		data.items = state.items
	}
	data.expiresAt = Date.now() + LOCAL_STORAGE_TTL_MS
	// Touch STW referral to extend expiry — matches Ember set-local-storage.js:100
	touchReferral(state.currentRef, 'skytabwebsite')
	try {
		localStorage.setItem(state.currentRef, JSON.stringify(data))
	} catch (error) {
		logWarning('[ticketSlice] Failed to persist cart to localStorage', { error })
	}
}

// ---------------------------------------------------------------------------
// restoreCart async thunk (STO-3073)
// ---------------------------------------------------------------------------

export const restoreCart = createAsyncThunk(
	'ticket/restoreCart',
	({
		ref,
		location,
		isDineIn = false,
	}: {
		ref: string
		location: LocationData
		isDineIn?: boolean
	}) => {
		// Referrals are stored under separate keys and must be hydrated even when there is no
		// cart blob — a user can visit via referral URL, clear their cart, and still be
		// attributed. TTL validation happens inside getReferral.
		const stwReferral = getReferral(ref, 'skytabwebsite')
		const owgReferral = getReferral(ref, 'owg')

		const raw = localStorage.getItem(ref)
		if (!raw) {
			const referralOnly: Partial<TicketState> = {}
			if (stwReferral) {
				referralOnly.referralGuid = stwReferral.value
				referralOnly.referralSource = stwReferral.source
			}
			if (owgReferral) {
				referralOnly.rwgToken = owgReferral.value
			}
			return Object.keys(referralOnly).length > 0 ? referralOnly : null
		}

		let stored: StoredTicketData
		let outerItems: CartItem[] | undefined
		try {
			const parsed = JSON.parse(raw) as unknown
			// Support Ember format { items: [], orderSettings: {...}, expiresAt: ... }
			if (typeof parsed === 'object' && parsed !== null && 'orderSettings' in parsed) {
				const wrapped = parsed as {
					orderSettings: StoredTicketData
					items?: CartItem[]
					expiresAt?: number
				}
				stored = wrapped.orderSettings
				if (wrapped.expiresAt !== undefined) stored.expiresAt = wrapped.expiresAt
				outerItems = Array.isArray(wrapped.items) ? wrapped.items : undefined
			} else {
				stored = parsed as StoredTicketData
			}
		} catch (error) {
			logWarning('[ticketSlice] Malformed localStorage data for ref, resetting cart', {
				ref,
				error,
			})
			return null
		}

		if (stored.expiresAt !== undefined && stored.expiresAt < Date.now()) {
			localStorage.removeItem(ref)
			return null
		}

		// Ember parity (ticket.js validateStorage): wipe only when a fresh QRPay dine-in URL
		// is active but the stored session is not dine-in — the stored cart belongs to a
		// different context. The reverse case (!isDineIn && stored=DINE_IN) is intentionally
		// not wiped: in-app navigation strips URL params, so isDineIn=false even while the
		// customer is mid dine-in session. The 2-hour TTL bounds stale sessions instead.
		if (isDineIn && stored.orderTypeGuid !== 'DINE_IN') {
			localStorage.removeItem(ref)
			return null
		}

		// Dine-in does not support deferred ordering. If a stored dine-in session has
		// isDeferred: true (e.g. set during a previous non-dine-in session then overwritten),
		// clear it and force the user back through Order Settings.
		if (stored.orderTypeGuid === 'DINE_IN' && stored.isDeferred) {
			localStorage.removeItem(ref)
			return {
				storeSettingsChangedError: 'orderSettings:timingUnavailableError',
				items: [] as CartItem[],
				// Explicitly reset all order-settings fields so they are cleared even if
				// Redux state was pre-seeded before restoreCart ran (e.g. multi-tab scenario).
				selectedOrderTypeGuid: null,
				selectedOrderType: null,
				selectedMenuSchedule: null,
				isDeferred: false,
				orderTimeUtc: null,
				expectedTimeUtc: null,
				customerSelectedDay: null,
			}
		}

		const result: Partial<TicketState> = {}

		if (stored.orderTypeGuid) {
			if (!location.orderTypes) {
				logWarning(
					'[ticketSlice] restoreCart: location.orderTypes is nullish — partial API response',
					{
						ref,
					},
				)
			}
			const match = (location.orderTypes ?? []).find((ot) => ot.ref === stored.orderTypeGuid)
			if (match) {
				result.selectedOrderTypeGuid = match.ref
				result.selectedOrderType = match
			} else {
				result.selectedOrderTypeGuid = null
				result.selectedOrderType = null
				result.storeSettingsChangedError = 'error:orderType:unavailable'
			}
		}

		if (stored.menuScheduleRef) {
			if (!location.menuSchedules) {
				logWarning(
					'[ticketSlice] restoreCart: location.menuSchedules is nullish — partial API response',
					{
						ref,
					},
				)
			}
			const match = (location.menuSchedules ?? []).find(
				(s) => s.ref === stored.menuScheduleRef,
			)
			if (match) {
				result.selectedMenuSchedule = match
			} else {
				result.selectedMenuSchedule = null
			}
		}

		if (stored.dineInTableName) result.dineInTableName = stored.dineInTableName
		if (stored.isDeferred !== undefined) result.isDeferred = stored.isDeferred
		if (stored.expectedTimeUtc) result.expectedTimeUtc = stored.expectedTimeUtc
		if (stored.orderTimeUtc) {
			result.orderTimeUtc = stored.orderTimeUtc
		} else if (result.isDeferred) {
			// Legacy/truncated data: orderTimeUtc absent for a deferred state.
			// Reset to ASAP so the availability query is not silently skipped.
			result.isDeferred = false
			result.expectedTimeUtc = null
			result.customerSelectedDay = null
		}
		if (stored.customerSelectedDay) result.customerSelectedDay = stored.customerSelectedDay
		if (stored.dropOffPointRef) result.dropOffPointRef = stored.dropOffPointRef
		if (stored.dropOffPointLastMileValue)
			result.dropOffPointLastMileValue = stored.dropOffPointLastMileValue
		// Always set (even to null) to prevent a previous location's address bleeding in on
		// re-restore — mirrors the items handling below. parseStoredDeliveryAddress validates
		// the shape so malformed/tampered storage cannot enter state.
		result.deliveryAddress = parseStoredDeliveryAddress(stored.deliveryAddress)
		if (stored.deliveryInstructions) result.deliveryInstructions = stored.deliveryInstructions
		if (stored.isContactlessDelivery)
			result.isContactlessDelivery = stored.isContactlessDelivery
		if (stored.discountCode) result.discountCode = stored.discountCode

		// Restore items — always set (even to []) to prevent location bleed.
		// Wrapped format stores items at the top level; flat format stores them inside StoredTicketData.
		// parseStoredItems validates each entry against CartItemSchema — malformed entries are
		// dropped with a warning rather than crashing or loading corrupt state.
		const rawItems = outerItems ?? stored.items
		result.items = parseStoredItems(rawItems).filter((item) => item.locationRef === ref)

		if (stwReferral) {
			result.referralGuid = stwReferral.value
			result.referralSource = stwReferral.source
		}
		if (owgReferral) {
			result.rwgToken = owgReferral.value
		}

		return result
	},
)

// ---------------------------------------------------------------------------
// buildCalculateRequest — pure helper, tested independently from the thunk
// ---------------------------------------------------------------------------

function serializeModifiers(modifiers: CartItemModifier[]): CalculateCartItemModifier[] {
	return modifiers.map((mod) => ({
		modifierSetRef: mod.modifierSetRef,
		modifierRef: mod.modifierRef,
		quantity: mod.quantity,
		modifiers: serializeModifiers(mod.modifiers), // recursive — handles unbounded nesting depth
	}))
}

export function buildCalculateRequest(
	state: TicketState,
	isAtCheckout = false,
): CalculateCartRequest {
	if (!state.selectedOrderTypeGuid) {
		throw new Error(
			'[buildCalculateRequest] selectedOrderTypeGuid must be set — call from the calculate thunk only after Step 1 guard',
		)
	}
	return {
		items: state.items.map((item) => ({
			ref: item.id, // client UUID — Ember sends this as 'ref' (primaryKey: 'ref' in application.js serializer)
			locationRef: item.locationRef,
			itemRef: item.itemRef,
			quantity: item.quantity,
			specialRequest: item.specialRequest,
			modifiers: serializeModifiers(item.modifiers),
		})),
		orderTypeRef: state.selectedOrderTypeGuid,
		menuScheduleRef: state.selectedMenuSchedule?.ref ?? null,
		isAtCheckout,
		// R29 (STO-3353): loyalty discount wired in;
		discounts: state.loyaltyDiscount
			? [
					{
						discountRef: state.loyaltyDiscount.discountRef,
						source: state.loyaltyDiscount.source,
					},
				]
			: [], // Ember always sends discounts: [] even when empty; P2-S5-04 expands this to loyalty/coupon
		couponCode: state.discountCode ?? undefined,
		// Ember: ticket.js:326 — data.tips = this.get('tipSettings') || undefined
		// Always send the static location tip configuration so the server returns tipAmount1/2/3.
		tips: state.tipSettings ?? undefined,
	}
}

// ---------------------------------------------------------------------------
// Slice
// ---------------------------------------------------------------------------

const ticketSlice = createSlice({
	name: 'ticket',
	initialState,
	reducers: {
		// --- Order settings (STO-3073 / STO-3095) ---

		// guid must equal orderType.ref — downstream selectors rely on this invariant
		setOrderType(state, action: PayloadAction<{ guid: string; orderType: OrderType }>) {
			state.selectedOrderTypeGuid = action.payload.guid
			state.selectedOrderType = action.payload.orderType
			// Reset min-amount alert state when order type changes — new order type has its own limits.
			state.showOrderTypeMinWarning = false
			state.orderTypeMinAlertNudge = 0
			// DSP quote is scoped to one order type — clear on change.
			// distanceLeadTimeInMinutes is NOT cleared here: must survive Confirm so the pre-confirm ETA is still readable.
			state.preferredQuoteId = null
			// Table name is only meaningful for DINE_IN — clear it when switching to any other type
			// so the header TablePill and ConfirmationPage dineIn href don't carry stale data.
			if (action.payload.guid !== 'DINE_IN') state.dineInTableName = null
			persistToLocalStorage(state)
		},

		setTable(state, action: PayloadAction<string>) {
			state.dineInTableName = action.payload
			persistToLocalStorage(state)
		},

		setSchedule(
			state,
			action: PayloadAction<{
				isDeferred: boolean
				expectedTimeUtc?: string | null
				orderTimeUtc?: string | null
				customerSelectedDay?: string | null
			}>,
		) {
			state.isDeferred = action.payload.isDeferred
			if (action.payload.isDeferred) {
				state.expectedTimeUtc = action.payload.expectedTimeUtc ?? null
				state.orderTimeUtc = action.payload.orderTimeUtc ?? null
				state.customerSelectedDay = action.payload.customerSelectedDay ?? null
			} else {
				state.expectedTimeUtc = null
				state.orderTimeUtc = null
				state.customerSelectedDay = null
			}
			persistToLocalStorage(state)
		},

		setReferral(state, action: PayloadAction<{ guid: string; source: string }>) {
			state.referralGuid = action.payload.guid
			state.referralSource = action.payload.source
		},

		setRwgToken(state, action: PayloadAction<string>) {
			state.rwgToken = action.payload
		},

		// rwgToken (OWG) is intentionally NOT cleared — OWG is singleUse:false and must
		// survive the session for repeat orders. Order payloads read OWG fresh from
		// localStorage (via buildOrderPayloadExtras), so stale Redux rwgToken has no impact.
		clearStwReferral(state) {
			state.referralGuid = null
			state.referralSource = null
		},

		setPendingReorder(state, action: PayloadAction<{ itemGuid: string; itemRef: string }>) {
			state.pendingReorder = action.payload
		},

		clearStoreSettingsError(state) {
			state.storeSettingsChangedError = null
		},

		setStoreSettingsChangedError(state, action: PayloadAction<string>) {
			state.storeSettingsChangedError = action.payload
		},

		setDineInAllowDuringOffHours(state, action: PayloadAction<boolean>) {
			state.dineInAllowDuringOffHours = action.payload
		},

		setNonDineInOrderTypeCount(state, action: PayloadAction<number>) {
			state.nonDineInOrderTypeCount = action.payload
		},

		setHasFutureSlotsForTakeout(state, action: PayloadAction<boolean | null>) {
			state.hasFutureSlotsForTakeout = action.payload
		},

		setHasFutureSlotsForDelivery(state, action: PayloadAction<boolean | null>) {
			state.hasFutureSlotsForDelivery = action.payload
		},

		// STO-3629: store location tip configuration so buildCalculateRequest can send it.
		// Ember: routes/location.js:137 — ticket.tipSettings = model.get('tipSettings')
		setTipSettings(state, action: PayloadAction<TipSettings>) {
			state.tipSettings = action.payload
		},

		// STO-3720: sync location tenders into Redux (same pattern as tipSettings).
		setTenders(state, action: PayloadAction<Tender[]>) {
			state.tenders = action.payload
		},

		setCurrentRef(state, action: PayloadAction<string>) {
			state.currentRef = action.payload
		},

		setMenuSchedule(state, action: PayloadAction<MenuSchedule>) {
			state.selectedMenuSchedule = action.payload
			persistToLocalStorage(state)
		},

		// Sets the schedule for the current session WITHOUT persisting to localStorage.
		// Used by MenuLayout's bootstrap default so that a stored schedule ref that couldn't
		// be matched (e.g. a time-gated schedule outside its active window) is not overwritten.
		// User-initiated selections must always use setMenuSchedule instead.
		initMenuSchedule(state, action: PayloadAction<MenuSchedule>) {
			state.selectedMenuSchedule = action.payload
		},

		setDeliveryAddress(state, action: PayloadAction<DeliveryAddress>) {
			state.deliveryAddress = action.payload
			state.dropOffPointRef = null
			state.dropOffPointLastMileValue = null
			persistToLocalStorage(state)
		},

		setDropOffPoint(
			state,
			action: PayloadAction<{
				dropOffPointRef: string
				dropOffPointLastMileValue: string | null
			}>,
		) {
			state.dropOffPointRef = action.payload.dropOffPointRef
			state.dropOffPointLastMileValue = action.payload.dropOffPointLastMileValue
			state.deliveryAddress = null
			persistToLocalStorage(state)
		},

		// Clears both address and drop-off selections without setting either — used for the
		// Q5b/Q5c mutual exclusion in DeliveryAddressSection when the user changes interaction mode.
		clearDeliverySelection(state) {
			state.deliveryAddress = null
			state.dropOffPointRef = null
			state.dropOffPointLastMileValue = null
			state.deliveryInstructions = null
			state.isContactlessDelivery = false
			state.deliveryEtaUtc = null
			state.distanceLeadTimeInMinutes = null
			state.preferredQuoteId = null
			persistToLocalStorage(state)
		},

		// STO-3691: store what the customer typed in Customer Information so it survives
		// navigating away from checkout. Partial payload — each field commits independently on
		// its own blur, so an untouched field must not be overwritten with undefined.
		setContactInfo(state, action: PayloadAction<Partial<TicketState['contactInfo']>>) {
			state.contactInfo = { ...state.contactInfo, ...action.payload }
		},

		// STO-3691: drop the retained contact fields. Called on logout so the next customer on a
		// shared device does not inherit the previous one's details.
		clearContactInfo(state) {
			state.contactInfo = {
				phone: null,
				email: null,
				firstName: null,
				lastName: null,
			}
		},

		// STO-3352 AC10: ETA returned by validate-distance — persists to Order Settings for slot filtering
		setDeliveryEtaUtc(state, action: PayloadAction<string | null>) {
			state.deliveryEtaUtc = action.payload
		},

		// STO-3425: DSP delivery ETA — set after validate-distance succeeds; cleared on order type change.
		setDistanceLeadTimeInMinutes(state, action: PayloadAction<number | null>) {
			state.distanceLeadTimeInMinutes = action.payload
		},

		// STO-3489: DSP preferred quote ID — stored after validate-distance succeeds for DSP orders.
		setPreferredQuoteId(state, action: PayloadAction<string | null>) {
			state.preferredQuoteId = action.payload
		},

		// STO-3352: delivery instructions and contactless — collected at checkout under ew-olo-checkout-address-collection flag
		setDeliveryInstructions(state, action: PayloadAction<string | null>) {
			state.deliveryInstructions = action.payload
			persistToLocalStorage(state)
		},

		// TODO: STO-3592 — wire isContactlessDelivery to API payload when backend supports true
		setIsContactlessDelivery(state, action: PayloadAction<boolean>) {
			state.isContactlessDelivery = action.payload
			persistToLocalStorage(state)
		},

		// --- Cart actions (STO-3130) ---

		// Caller provides all CartItem fields except id — the store generates the UUID.
		// This ensures id is always a proper UUID and callers cannot inject arbitrary ids.
		addItem(state, action: PayloadAction<Omit<CartItem, 'id'>>) {
			state.items.push({ ...action.payload, id: crypto.randomUUID() })
			persistToLocalStorage(state)
		},

		// Removes by cart item UUID. No-op if the id is not found.
		removeItem(state, action: PayloadAction<string>) {
			state.items = state.items.filter((item) => item.id !== action.payload)
			persistToLocalStorage(state)
		},

		// Removes all items matching a menu item ref (itemRef).
		// Also removes orphaned items where itemRef is empty — mirrors Ember's
		// !cartItem.get('item.id') guard in ticket.js:374.
		// Used exclusively by the calculate thunk's ITMNA recovery path.
		removeItemsByMenuRef(state, action: PayloadAction<string>) {
			state.items = state.items.filter(
				(item) => item.itemRef !== action.payload && !!item.itemRef,
			)
			persistToLocalStorage(state)
		},

		// Replaces the matching item in-place. Array length is unchanged.
		// No-op if the id is not found. Used by the edit item flow.
		updateItem(state, action: PayloadAction<CartItem>) {
			const index = state.items.findIndex((item) => item.id === action.payload.id)
			if (index !== -1) {
				state.items[index] = action.payload
				persistToLocalStorage(state)
			}
		},

		// Updates quantity only — all other fields unchanged.
		updateItemQuantity(state, action: PayloadAction<{ itemId: string; quantity: number }>) {
			const item = state.items.find((i) => i.id === action.payload.itemId)
			if (item) {
				item.quantity = action.payload.quantity
				persistToLocalStorage(state)
			}
		},

		// --- Tip actions (STO-3130 — state only; wired into calculate request in P2-S3-09) ---

		setTip(
			state,
			action: PayloadAction<
				{ type: 'percent'; percentage: number } | { type: 'custom'; amount: number }
			>,
		) {
			if (action.payload.type === 'percent') {
				state.selectedTip = 'percent'
				state.tipPercentage = action.payload.percentage
				state.tipCustomAmount = null
			} else {
				state.selectedTip = 'custom'
				state.tipCustomAmount = action.payload.amount
				state.tipPercentage = null
			}
			// TODO: P2-S3-09 — persist tip selection here
		},

		clearTip(state) {
			state.selectedTip = null
			state.tipPercentage = null
			state.tipCustomAmount = null
		},

		// --- Loyalty discount (STO-3353) ---
		// R3: always dispatch resetLoyalty() + clearLoyaltyDiscount() + calculate() together.
		setLoyaltyDiscount(state, action: PayloadAction<{ discountRef: string; source: string }>) {
			state.loyaltyDiscount = action.payload
			state.loyaltyDiscountError = null
		},

		clearLoyaltyDiscount(state) {
			state.loyaltyDiscount = null
			state.loyaltyDiscountError = null
		},

		setLoyaltyDiscountError(state, action: PayloadAction<string | null>) {
			state.loyaltyDiscountError = action.payload
		},

		// --- Discount code reducers (S5-04) ---

		// Setting a new code clears any prior error and resets the valid flag — the
		// code must be re-validated via the calculate thunk before it is trusted.
		setDiscountCode(state, action: PayloadAction<string | null>) {
			state.discountCode = action.payload
			state.discountName = null
			state.discountCodeError = null
			state.discountCodeValid = false
			persistToLocalStorage(state)
		},

		setDiscountName(state, action: PayloadAction<string | null>) {
			state.discountName = action.payload
		},

		setDiscountCodeError(state, action: PayloadAction<string | null>) {
			state.discountCodeError = action.payload
		},

		setDiscountCodeErrorCode(state, action: PayloadAction<string | null>) {
			state.discountCodeErrorCode = action.payload
		},

		setDiscountCodeLoading(state, action: PayloadAction<boolean>) {
			state.discountCodeLoading = action.payload
		},

		setDiscountCodeValid(state, action: PayloadAction<boolean>) {
			state.discountCodeValid = action.payload
		},

		// Full reset — used by the ORDCOUPINV recovery path in calculate() and by the
		// component's "Remove" button. discountCodeLoading is owned by the UI layer —
		// the component clears it via setDiscountCodeLoading after dispatching this.
		clearDiscountCode(state) {
			state.discountCode = null
			state.discountName = null
			state.discountCodeError = null
			state.discountCodeErrorCode = null
			state.discountCodeValid = false
			persistToLocalStorage(state)
		},

		// --- Gift card reducers (S5-04) ---

		// Setting a card number clears any prior error so the UI hides stale messages.
		setGiftCardNumber(state, action: PayloadAction<string | null>) {
			state.giftCardNumber = action.payload
			state.giftCardErrors = null
		},

		setGiftCardBalance(state, action: PayloadAction<number | null>) {
			state.giftCardBalance = action.payload
		},

		setGiftCardAppliedAmount(state, action: PayloadAction<number | null>) {
			state.giftCardAppliedAmount = action.payload
		},

		setGiftCardErrors(state, action: PayloadAction<string | null>) {
			state.giftCardErrors = action.payload
		},

		// Clears the entire gift card entry (number + balance + errors).
		// Used when the user removes the gift card from the checkout screen.
		clearGiftCardNumber(state) {
			state.giftCardNumber = null
			state.giftCardBalance = null
			state.giftCardErrors = null
		},

		// Clears only the applied redemption amount, leaving the card on file.
		// Used when the user un-applies a partial or full gift card payment without
		// removing the card itself (Ember: clearGiftCardAppliedBalance / clearGiftCardDetails).
		clearGiftCardAppliedBalance(state) {
			state.giftCardAppliedAmount = null
		},

		// --- Payment tender reducer (S5-04) ---

		setTenderTypeGuid(
			state,
			action: PayloadAction<{ tenderRef: string | null; isTipAllowed: boolean }>,
		) {
			state.tenderTypeGuid = action.payload.tenderRef
			state.isTipDisabledForSelectedTender = !action.payload.isTipAllowed
		},

		// Called by the cart UI after displaying the "items removed" notification toast.
		// The UI component owns the dismiss timer — do not auto-clear here.
		clearItemsRemovedCount(state) {
			state.itemsRemovedCount = 0
		},

		// Set by the calculate thunk when ITMOOSTK errors are returned (flag ON).
		// UI navigates to the sold-out warning page; page reads this to list affected items.
		setItemsOutOfStock(state, action: PayloadAction<string[]>) {
			state.itemsOutOfStock = action.payload
		},

		// Called by SoldOutWarningModal after the user confirms removal.
		clearItemsOutOfStock(state) {
			state.itemsOutOfStock = []
		},

		// Called by CartActionsFooter when checkout is blocked by an unmet minimum.
		// If the warning was already showing, bumps the nudge counter (triggers shake animation).
		// If not yet showing, sets the flag so the alert becomes visible.
		triggerOrderTypeMinWarning(state) {
			if (state.showOrderTypeMinWarning) {
				state.orderTypeMinAlertNudge += 1
			} else {
				state.showOrderTypeMinWarning = true
			}
		},

		// Called when the cart total drops back below the minimum (from items being removed).
		// Resets both alert visibility and nudge count.
		resetOrderTypeMinWarning(state) {
			state.showOrderTypeMinWarning = false
			state.orderTypeMinAlertNudge = 0
		},

		// Clears the full cart and all derived state.
		// Ember parity: ticket.js clearCart() called calculate() after clearing, which
		// immediately hit the early exit (items.length === 0). In React the thunk's
		// early exit achieves the same result — we do not dispatch calculate() from here.
		clearCart(state) {
			if (state.currentRef) {
				try {
					localStorage.removeItem(state.currentRef)
				} catch (error) {
					logWarning('[ticketSlice] Failed to remove cart from localStorage', { error })
				}
			}
			state.items = []
			state.isCalculating = false
			state.calculations = null
			state.calculateErrors = null
			state.itemsRemovedCount = 0
			state.itemsOutOfStock = []
			state.showOrderTypeMinWarning = false
			state.orderTypeMinAlertNudge = 0
			state.selectedTip = null
			state.tipPercentage = null
			state.tipCustomAmount = null
			state.loyaltyDiscount = null
			state.loyaltyDiscountError = null
			state.preferredQuoteId = null
			// Prevent stale instructions leaking into the next order via persistToLocalStorage.
			state.deliveryInstructions = null
			// STO-3691: the retained contact fields belong to the order being abandoned/completed —
			// a fresh cart starts a fresh order, so they must not leak into it.
			state.contactInfo = {
				phone: null,
				email: null,
				firstName: null,
				lastName: null,
			}
		},

		// Ember parity: ticket.js clearOrderSettings() — called from confirmation.js willTransition
		// when the submitted order status is 'ok'. Resets all order-type and schedule selections
		// so the user is directed to Order Settings on return to the menu, where current availability
		// (including active throttling windows) is re-evaluated before a new order can be started.
		//
		// DSP quote fields and deliveryAddress are order-scoped — clear them so the next order
		// triggers a fresh validate-distance call rather than restoring stale data (STO-3682).
		//
		// Intentionally does NOT call persistToLocalStorage — Ember's clearOrderSettings() uses
		// setProperties() only, with no localStorage write. clearCart() (dispatched on checkout page
		// before navigating to Confirmation) has already removed the localStorage entry via
		// removeItem(). Calling persistToLocalStorage here would re-create that entry using stale
		// dineInTableName / dropOffPointRef / deliveryInstructions from Redux state, undoing
		// clearCart's removal. The next user-driven action (Order Settings selection) writes fresh
		// values via setOrderSettings → persistToLocalStorage, so no intermediate write is needed.
		clearOrderSettings(state) {
			state.selectedOrderTypeGuid = null
			state.selectedOrderType = null
			state.selectedMenuSchedule = null
			state.isDeferred = false
			state.expectedTimeUtc = null
			state.orderTimeUtc = null
			state.customerSelectedDay = null
			state.deliveryEtaUtc = null
			state.distanceLeadTimeInMinutes = null
			state.preferredQuoteId = null
			state.deliveryAddress = null
		},

		// --- Internal actions used only by the calculate thunk ---

		setIsCalculating(state, action: PayloadAction<boolean>) {
			state.isCalculating = action.payload
		},

		setCalculations(state, action: PayloadAction<CalculationResponse | null>) {
			state.calculations = action.payload
		},

		setCalculateErrors(state, action: PayloadAction<CalculationDomainError[] | null>) {
			state.calculateErrors = action.payload
		},

		setItemsRemovedCount(state, action: PayloadAction<number>) {
			state.itemsRemovedCount = action.payload
		},
	},
	extraReducers: (builder) => {
		builder.addCase(restoreCart.fulfilled, (state, action) => {
			state.currentRef = action.meta.arg.ref
			state.cartRestored = true
			// Reset deliveryAddress unconditionally before the null-payload guard.
			// restoreCart returns null when no cart exists, TTL has expired, JSON parsing
			// fails, or dine-in context does not match. All these paths previously skipped
			// this reset, leaving a previous location's address in state. Moving it here
			// ensures each restore — with or without a cart — always starts clean.
			state.deliveryAddress = null
			if (!action.payload) {
				state.deliveryInstructions = null
				return
			}
			Object.assign(state, action.payload)
		})
	},
})

// ---------------------------------------------------------------------------
// calculate plain thunk
//
// Plain thunk (not createAsyncThunk) because the ITMNA recovery path needs to
// dispatch actions and re-dispatch calculate() in a controlled sequence.
// createAsyncThunk's pending/fulfilled/rejected lifecycle makes that awkward.
//
// Ember equivalent: ticket.js calculate() method (lines 309–419).
// ---------------------------------------------------------------------------

// Monotonic counter for Step 10 gift card balance fetches.
// isCalculating is cleared before the balance await, so a concurrent calculate()
// can pass the inflight guard during the fetch window. The token lets Step 10
// detect when a newer invocation has superseded the current one and bail out.
let _balanceFetchSeq = 0

export const calculate =
	(args?: { isAtCheckout?: boolean; revalidating?: boolean; loyaltyTriggered?: boolean }) =>
	async (dispatch: AppDispatch, getState: () => RootState): Promise<void> => {
		const state = getState()
		const ticket = state.ticket

		// Step 1 — Early exit: mirrors Ember ticket.js lines 310–312.
		// No network call when cart is empty, order type is not set, or location not yet bootstrapped.
		if (ticket.items.length === 0 || !ticket.selectedOrderTypeGuid || !ticket.currentRef) {
			dispatch(ticketSlice.actions.setCalculations(null))
			dispatch(ticketSlice.actions.setCalculateErrors(null))
			return
		}

		// Step 2 — Inflight guard: prevents parallel calculate calls.
		if (ticket.isCalculating) return

		// Step 3 — Mark as in-flight.
		dispatch(ticketSlice.actions.setIsCalculating(true))

		// Step 4 — Build request body. Snapshot its JSON fingerprint so we can
		// detect any mutation to calculation inputs (items, quantities, modifiers,
		// specialRequest, orderType, schedule) that occurs while the call is in-flight.
		// Declared outside try/catch so the catch block can reference it for staleness checks.
		const body = buildCalculateRequest(ticket, args?.isAtCheckout ?? false)
		const requestFingerprintAtStart = JSON.stringify(body)

		try {
			// Step 5 — Call API.
			const response = await calculateCart(ticket.currentRef, body)

			// Step 6 — Success. Re-check all calculation inputs against the fingerprint
			// taken before the API call. Two staleness conditions must be handled:
			//   a) Cart emptied while in-flight — applying a non-zero total would leave
			//      Amount Due showing the old value (STO-3273).
			//   b) Any calculation input mutated while in-flight (quantity, modifier,
			//      specialRequest, orderType, schedule) — the response reflects the old
			//      request, not the current one. Reset the guard and re-fire.
			const currentTicket = getState().ticket
			if (currentTicket.items.length === 0) {
				dispatch(ticketSlice.actions.setCalculations(null))
			} else {
				const requestFingerprintNow = JSON.stringify(
					buildCalculateRequest(currentTicket, args?.isAtCheckout ?? false),
				)
				if (requestFingerprintNow !== requestFingerprintAtStart) {
					// Inputs changed while in-flight — discard stale response and recalculate.
					dispatch(ticketSlice.actions.setIsCalculating(false))
					await dispatch(calculate(args))
					return
				}
				dispatch(ticketSlice.actions.setCalculations(response))
			}
			dispatch(ticketSlice.actions.setCalculateErrors(null))
			dispatch(ticketSlice.actions.setIsCalculating(false))
		} catch (err) {
			// Staleness guard for the error path — mirrors the Step 6 check.
			// Re-snapshot the cart now that the API call has completed. If the cart was
			// emptied or mutated while in-flight the error response is stale: ITMNA refs
			// may no longer exist, ITMOOSTK refs may be for items already removed, and
			// generic error state would incorrectly override a successful subsequent call.
			const currentTicketOnError = getState().ticket
			const isCartEmptyNow = currentTicketOnError.items.length === 0
			const requestFingerprintNow = isCartEmptyNow
				? null
				: JSON.stringify(
						buildCalculateRequest(currentTicketOnError, args?.isAtCheckout ?? false),
					)
			const isStale = isCartEmptyNow || requestFingerprintNow !== requestFingerprintAtStart

			if (err instanceof CartApiError) {
				// Step 7 — ITMNA recovery: items no longer available on the menu.
				// Collect unique menu item refs from all ITMNA errors.
				const itmnaRefs = [
					...new Set(
						err.errors
							.filter((e) => e.code === 'ITMNA')
							.map((e) => {
								const ref = e.metadata?.InvalidItemRef
								return typeof ref === 'string' ? ref : null
							})
							.filter((ref): ref is string => ref !== null && ref.length > 0),
					),
				]

				if (itmnaRefs.length > 0) {
					// Only proceed with ITMNA removal if the refs still exist in the current cart.
					// A stale request may flag refs that the user already removed — attempting to
					// remove them again is a no-op, but the subsequent retry would fire with an
					// unchanged cart and loop indefinitely. Guard against that here.
					const stillPresent = itmnaRefs.some((ref) =>
						currentTicketOnError.items.some((item) => item.itemRef === ref),
					)
					if (stillPresent) {
						const countBefore = getState().ticket.items.length
						itmnaRefs.forEach((ref) =>
							dispatch(ticketSlice.actions.removeItemsByMenuRef(ref)),
						)
						const countAfter = getState().ticket.items.length

						if (countAfter < countBefore) {
							// Items were actually removed — retry makes progress.
							dispatch(
								ticketSlice.actions.setItemsRemovedCount(countBefore - countAfter),
							)
							// Critical: reset isCalculating BEFORE re-dispatching calculate.
							// If isCalculating remains true, the inflight guard in Step 2 silently
							// blocks the retry and the cart never recovers.
							// Pass args through so isAtCheckout is preserved on the retry call.
							dispatch(ticketSlice.actions.setIsCalculating(false))
							await dispatch(calculate(args))
							return // do not fall through to Step 9
						}
					}
					// Nothing removed (or all ITMNA refs already gone from a concurrent mutation) —
					// fall through to Step 9 rather than retrying with an identical request.
				}

				// Step 8 — Deferred error branches.

				// ITMOOSTK — only runs if no ITMNA errors were present (ITMNA returned early above).
				// If the request is stale the out-of-stock refs may be for items that no longer
				// exist — skip to avoid sending the user to the sold-out page for phantom items.
				if (!isStale) {
					const itmoostkRefs = [
						...new Set(
							err.errors
								.filter((e) => e.code === 'ITMOOSTK')
								.map((e) => {
									const ref = e.metadata?.InvalidItemRef
									return typeof ref === 'string' ? ref : null
								})
								.filter((ref): ref is string => ref !== null && ref.length > 0),
						),
					]
					if (itmoostkRefs.length > 0) {
						dispatch(ticketSlice.actions.setItemsOutOfStock(itmoostkRefs))
						dispatch(ticketSlice.actions.setIsCalculating(false))
						// CALC-2: Ember's finally block runs unconditionally — including after ITMOOSTK.
						// Run the same post-calculate side-effects before rejecting so gift card
						// revalidation, coupon validation, and CASH coercion are not skipped.
						// Clear any stale calculateErrors from a prior failed attempt so the
						// calculateErrors gate in runPostCalculateSideEffects() does not short-circuit.
						dispatch(ticketSlice.actions.setCalculateErrors(null))
						await runPostCalculateSideEffects()
						return Promise.reject(new Error('ITMOOSTK'))
					}
				}

				// ORDCOUPINV — coupon code invalid; auto-retry once without coupon.
				// Guard with !args?.revalidating to prevent an infinite retry loop when the
				// server repeatedly rejects a code (shouldn't happen, but defensive).
				const ordCoupInvError = err.errors.find((e) => e.code === 'ORDCOUPINV')
				if (ordCoupInvError !== undefined && !args?.revalidating && !isStale) {
					// Capture the code before clearDiscountCode() nulls it — needed for error interpolation.
					const invalidCode = getState().ticket.discountCode
					dispatch(ticketSlice.actions.clearDiscountCode())
					dispatch(
						ticketSlice.actions.setDiscountCodeError(
							ordCoupInvError.friendlyError ?? 'service:ticket:coupon-invalid:error',
						),
					)
					// When the API provides no friendlyError, the UI will interpolate {code} from this.
					if (!ordCoupInvError.friendlyError && invalidCode) {
						dispatch(ticketSlice.actions.setDiscountCodeErrorCode(invalidCode))
					}
					dispatch(ticketSlice.actions.setIsCalculating(false))
					await dispatch(calculate({ ...args, revalidating: true }))
					return
				}
				// ORDCOUPINSTOT — coupon code was structurally valid but the order total is too
				// low to meet the coupon's minimum. Clear the code, surface the error, then
				// retry without the coupon so the cart breakdown stays intact.
				const ordCoupInsTotError = err.errors.find((e) => e.code === 'ORDCOUPINSTOT')
				if (ordCoupInsTotError !== undefined && !args?.revalidating && !isStale) {
					dispatch(ticketSlice.actions.clearDiscountCode())
					dispatch(
						ticketSlice.actions.setDiscountCodeError(
							ordCoupInsTotError.friendlyError ??
								'service:ticket:discount:insufficient-total:error',
						),
					)
					dispatch(ticketSlice.actions.setIsCalculating(false))
					await dispatch(calculate({ ...args, revalidating: true }))
					return
				}
			}

			// Step 9 — Non-recoverable: unknown domain errors, network failures, or schema errors.
			// ApiError with SCHEMA_ERROR arrives here when the server returns an unexpected shape —
			// surface it as a cart error so the UI shows a failure state rather than null (clean cart).
			// If the request is stale, silently discard — a concurrent call already owns the result.
			if (isStale) {
				dispatch(ticketSlice.actions.setIsCalculating(false))
				await dispatch(calculate(args))
				return
			}

			// Loyalty-triggered failure: the calculate was fired by a Redeem click and the server
			// rejected it (e.g. loyalty program deactivated — returns a generic 500 with no error
			// codes). Clear the discount selection, surface a per-row error on the offending reward,
			// and leave calculations intact so Amount Due stays unchanged (v1 parity).
			if (args?.loyaltyTriggered && currentTicketOnError.loyaltyDiscount !== null) {
				const failedDiscountRef = currentTicketOnError.loyaltyDiscount.discountRef
				dispatch(clearLoyaltyDiscount())
				dispatch(ticketSlice.actions.setLoyaltyDiscountError(failedDiscountRef))
				dispatch(ticketSlice.actions.setIsCalculating(false))
				return
			}

			let calculateErrors: CalculationDomainError[] | null = null
			if (err instanceof CartApiError) {
				calculateErrors = err.errors
			} else if (err instanceof ApiError) {
				calculateErrors = [{ code: err.code }]
			}
			dispatch(ticketSlice.actions.setCalculateErrors(calculateErrors))
			// Note: calculations intentionally not nulled out here — the last successful
			// Amount Due remains visible while checkout is blocked by calculateErrors (v1 parity).
			dispatch(ticketSlice.actions.setIsCalculating(false))
		}

		// Step 10 — Post-calculate side-effects from ticket.js lines 395–418.
		// Extracted into a named helper so it can be called on every exit path including
		// ITMOOSTK (CALC-2 parity: Ember's finally block runs unconditionally).
		// Does NOT run on early-exit (Step 1) or ITMNA/ORDCOUPINV recovery (those return early above).
		async function runPostCalculateSideEffects() {
			// Each effect is gated on no calculateErrors so API failures don't trigger them.
			const postCalcTicket = getState().ticket
			if (postCalcTicket.calculateErrors) return

			// Coupon validation — Ember: ticket.js:401–408
			// ORDCOUPINV and ORDCOUPINSTOT both clear discountCode before reaching here,
			// so any storedCode at this point was implicitly accepted by the server.
			// Use activeCouponCode for strict confirmation when the API provides it;
			// fall back to "accepted" when it's absent (older API versions omit it).
			const storedCode = postCalcTicket.discountCode
			if (storedCode !== null) {
				const confirmedCode = postCalcTicket.calculations?.activeCouponCode
				const isValid = confirmedCode !== undefined ? confirmedCode === storedCode : true
				dispatch(setDiscountCodeValid(isValid))
				if (isValid) {
					// Coupon name lives in the top-level discounts[] entry where source === 'coupon'.
					const couponDiscount = postCalcTicket.calculations?.discounts?.find(
						(d) => d.source === 'coupon',
					)
					dispatch(ticketSlice.actions.setDiscountName(couponDiscount?.name ?? null))
				} else {
					dispatch(ticketSlice.actions.setDiscountName(null))
				}
			}

			// Ember parity: reApplyGiftCardAmount (ticket.js:396–399, 599–618)
			// After every successful recalculation, if a gift card has been applied, cap the
			// applied amount to the new grand total. This handles cases where the total
			// decreased (loyalty reward, coupon) after the gift card was already applied —
			// the applied amount must not exceed the new amount due.
			// When grandTotal drops to 0 (fully discounted order), clear the applied amount.
			//
			// Capping is performed in two phases:
			//   Phase 1 — unconditional cap to grandTotal (no API call needed).
			//              Ensures the applied amount never overshoots, even if the
			//              balance fetch fails or is superseded by a newer invocation.
			//   Phase 2 — fresh balance fetch to detect cross-device usage.
			//              May further reduce the applied amount if the balance dropped.
			const giftCardNum = postCalcTicket.giftCardNumber
			const prevApplied = postCalcTicket.giftCardAppliedAmount
			if (prevApplied !== null && giftCardNum !== null) {
				const locationRef = postCalcTicket.currentRef
				if (locationRef === null) return

				// Phase 1 — Always cap to grand total.
				// Reads grandTotal from postCalcTicket (already updated by the calculate response).
				const currentGrandTotal = postCalcTicket.calculations?.totals.grandTotal ?? 0
				const cappedToGrandTotal = Math.min(prevApplied, currentGrandTotal)
				if (cappedToGrandTotal > 0) {
					dispatch(ticketSlice.actions.setGiftCardAppliedAmount(cappedToGrandTotal))
					dispatch(ticketSlice.actions.setGiftCardErrors(null))
				} else {
					// Grand total is $0 — gift card has nothing to apply to; clear it entirely
					// so GiftCardSection returns to State 1 (no card entered).
					dispatch(ticketSlice.actions.setGiftCardAppliedAmount(null))
					dispatch(ticketSlice.actions.clearGiftCardNumber())
				}

				// Phase 2 — Fetch fresh balance to handle cross-device card usage.
				try {
					const fetchToken = ++_balanceFetchSeq
					const newBalance = await getGiftCardBalance(locationRef, giftCardNum)
					// If a newer calculate() invocation started its own balance fetch while we
					// were awaiting, discard our stale result rather than overwriting theirs.
					// Phase 1 has already capped to grandTotal, so the applied amount is safe.
					if (_balanceFetchSeq !== fetchToken) return
					// Re-read state: the card may have been removed or re-applied while in flight.
					const afterFetch = getState().ticket
					if (
						afterFetch.giftCardNumber !== giftCardNum ||
						afterFetch.giftCardAppliedAmount === null
					) {
						return
					}
					const newGrandTotal = afterFetch.calculations?.totals.grandTotal ?? 0
					dispatch(ticketSlice.actions.setGiftCardBalance(newBalance))
					const newApplied = Math.min(
						afterFetch.giftCardAppliedAmount,
						newBalance,
						newGrandTotal,
					)
					dispatch(
						ticketSlice.actions.setGiftCardAppliedAmount(
							newApplied > 0 ? newApplied : null,
						),
					)
				} catch {
					if (getState().ticket.giftCardNumber === giftCardNum) {
						dispatch(ticketSlice.actions.setGiftCardBalance(null))
					}
				}
			}

			// CALC-1: Coerce tender to CASH when isCustomCashPayment — Ember: ticket.js lines 410–412.
			// isCustomCashPayment = dueAmount === 0 && no gift card && not a DSP order.
			// isTipAllowed: false — CASH isTipAllowed is false on all known locations.
			// If a location ever sets CASH isTipAllowed=true, read it from getState().ticket.tenders here.
			const isCustomCashPayment = selectIsCustomCashPayment(getState())
			if (isCustomCashPayment) {
				if (getState().ticket.tenderTypeGuid !== 'CASH') {
					dispatch(
						ticketSlice.actions.setTenderTypeGuid({
							tenderRef: 'CASH',
							isTipAllowed: false,
						}),
					)
				}
			}
			// CALC-1: reverse coercion — Ember ticket.js:413–415.
			// When !isCustomCashPayment && tenderTypeGuid === 'CASH' && no active CASH in location tenders
			// → reset to NON_CASH. Matches CheckoutPage cashTender lookup (ref === 'CASH' && isActive).
			// tenders.length > 0 guard prevents coercion before useLocationBootstrap has synced tenders.
			// ?? [] guards against partial preloaded Redux state in tests (tenders is non-nullable
			// in production but tests may omit it from their preloadedState object).
			const locationTenders = getState().ticket.tenders ?? []
			if (
				locationTenders.length > 0 &&
				!isCustomCashPayment &&
				getState().ticket.tenderTypeGuid === 'CASH' &&
				!locationTenders.some((t) => t.ref === 'CASH' && t.isActive)
			) {
				// No isActive check on nonCashTender — Ember parity: isTipAllowed is read from the
				// NON_CASH tender definition regardless of its active state.
				const nonCashTender = locationTenders.find((t) => t.ref === 'NON_CASH')
				dispatch(
					ticketSlice.actions.setTenderTypeGuid({
						tenderRef: 'NON_CASH',
						isTipAllowed: nonCashTender?.isTipAllowed ?? true,
					}),
				)
			}
		}

		await runPostCalculateSideEffects()
	}

// ---------------------------------------------------------------------------
// Action exports
// ---------------------------------------------------------------------------

export const {
	setOrderType,
	setTable,
	setSchedule,
	setReferral,
	setRwgToken,
	clearStwReferral,
	setPendingReorder,
	clearStoreSettingsError,
	setStoreSettingsChangedError,
	setDineInAllowDuringOffHours,
	setNonDineInOrderTypeCount,
	setHasFutureSlotsForTakeout,
	setHasFutureSlotsForDelivery,
	setTipSettings,
	setTenders,
	setCurrentRef,
	setMenuSchedule,
	initMenuSchedule,
	setDeliveryAddress,
	setDropOffPoint,
	clearDeliverySelection,
	setContactInfo,
	clearContactInfo,
	setDeliveryEtaUtc,
	setDistanceLeadTimeInMinutes,
	setPreferredQuoteId,
	setDeliveryInstructions,
	setIsContactlessDelivery,
	addItem,
	removeItem,
	removeItemsByMenuRef,
	updateItem,
	updateItemQuantity,
	setTip,
	clearTip,
	setLoyaltyDiscount,
	clearLoyaltyDiscount,
	setLoyaltyDiscountError,
	// --- S5-04: Discount code ---
	setDiscountCode,
	setDiscountCodeError,
	setDiscountCodeErrorCode,
	setDiscountCodeLoading,
	setDiscountCodeValid,
	clearDiscountCode,
	// --- S5-04: Gift card ---
	setGiftCardNumber,
	setGiftCardBalance,
	setGiftCardAppliedAmount,
	setGiftCardErrors,
	clearGiftCardNumber,
	clearGiftCardAppliedBalance,
	// --- S5-04: Payment tender ---
	setTenderTypeGuid,
	clearItemsRemovedCount,
	setItemsRemovedCount,
	setItemsOutOfStock,
	clearItemsOutOfStock,
	triggerOrderTypeMinWarning,
	resetOrderTypeMinWarning,
	clearCart,
	// Ember parity: confirmation.js willTransition → ticket.clearOrderSettings().
	// Only dispatch on successful order — not on failure (user may retry from checkout).
	clearOrderSettings,
	// AC14 / willTransition cleanup only. Always dispatch with null from outside the slice.
	// Non-null values must go through the calculate() thunk to preserve the
	// calculations ⊕ calculateErrors mutual-exclusivity invariant.
	setCalculations,
} = ticketSlice.actions

// ---------------------------------------------------------------------------
// Selectors
// ---------------------------------------------------------------------------

// --- Order settings selectors (STO-3073) ---
export const selectSelectedOrderTypeGuid = (state: { ticket: TicketState }) =>
	state.ticket.selectedOrderTypeGuid
export const selectSelectedOrderType = (state: { ticket: TicketState }) =>
	state.ticket.selectedOrderType
export const selectIsDeferred = (state: { ticket: TicketState }) => state.ticket.isDeferred
export const selectExpectedTimeUtc = (state: { ticket: TicketState }) =>
	state.ticket.expectedTimeUtc
export const selectOrderTimeUtc = (state: { ticket: TicketState }) => state.ticket.orderTimeUtc
export const selectCustomerSelectedDay = (state: { ticket: TicketState }) =>
	state.ticket.customerSelectedDay
export const selectDineInTableName = (state: { ticket: TicketState }) =>
	state.ticket.dineInTableName
export const selectDineInAllowDuringOffHours = (state: { ticket: TicketState }) =>
	state.ticket.dineInAllowDuringOffHours
export const selectStoreSettingsChangedError = (state: { ticket: TicketState }) =>
	state.ticket.storeSettingsChangedError
export const selectSelectedMenuSchedule = (state: { ticket: TicketState }) =>
	state.ticket.selectedMenuSchedule
export const selectDropOffPointRef = (state: { ticket: TicketState }) =>
	state.ticket.dropOffPointRef
export const selectDropOffPointLastMileValue = (state: { ticket: TicketState }) =>
	state.ticket.dropOffPointLastMileValue
export const selectDeliveryAddress = (state: { ticket: TicketState }) =>
	state.ticket.deliveryAddress
export const selectDeliveryInstructions = (state: { ticket: TicketState }) =>
	state.ticket.deliveryInstructions
// STO-3691: retained Customer Information values — read at ContactInformation mount.
export const selectContactInfo = (state: { ticket: TicketState }) => state.ticket.contactInfo
export const selectIsContactlessDelivery = (state: { ticket: TicketState }) =>
	state.ticket.isContactlessDelivery
export const selectDeliveryEtaUtc = (state: { ticket: TicketState }) => state.ticket.deliveryEtaUtc
export const selectNonDineInOrderTypeCount = (state: { ticket: TicketState }) =>
	state.ticket.nonDineInOrderTypeCount
export const selectHasFutureSlotsForTakeout = (state: { ticket: TicketState }) =>
	state.ticket.hasFutureSlotsForTakeout
export const selectHasFutureSlotsForDelivery = (state: { ticket: TicketState }) =>
	state.ticket.hasFutureSlotsForDelivery

// --- Cart selectors (STO-3130) ---
export const selectCartItems = (state: { ticket: TicketState }) => state.ticket.items
export const selectIsCalculating = (state: { ticket: TicketState }) => state.ticket.isCalculating
export const selectCalculations = (state: { ticket: TicketState }) => state.ticket.calculations
export const selectCalculateErrors = (state: { ticket: TicketState }) =>
	state.ticket.calculateErrors
export const selectItemsRemovedCount = (state: { ticket: TicketState }) =>
	state.ticket.itemsRemovedCount
export const selectItemsOutOfStock = (state: { ticket: TicketState }) =>
	state.ticket.itemsOutOfStock

// --- Tip selectors (STO-3130) ---
export const selectSelectedTip = (state: { ticket: TicketState }) => state.ticket.selectedTip
export const selectTipPercentage = (state: { ticket: TicketState }) => state.ticket.tipPercentage
export const selectTipCustomAmount = (state: { ticket: TicketState }) =>
	state.ticket.tipCustomAmount

// Memoised — iterates items[] to sum quantities.
// Returns total item count (sum of quantities, not array length).
// e.g. 3 items of quantity 2 each → 6, not 3.
// Used by MenuHeader cart badge (STO-3099).
export const selectCartItemCount = createSelector(
	(state: { ticket: TicketState }) => state.ticket.items,
	(items) => items.reduce((sum, item) => sum + item.quantity, 0),
)

// Memoised — reads server-confirmed total from nested calculations response.
// Returns 0 when no calculation has run yet (safe default for cart display).
export const selectCartTotal = createSelector(
	(state: { ticket: TicketState }) => state.ticket.calculations,
	(calculations) => calculations?.totals.grandTotal ?? 0,
)

export const selectShowOrderTypeMinWarning = (state: { ticket: TicketState }) =>
	state.ticket.showOrderTypeMinWarning
export const selectOrderTypeMinAlertNudge = (state: { ticket: TicketState }) =>
	state.ticket.orderTypeMinAlertNudge

// --- S5-04: Discount code selectors ---
export const selectDiscountCode = (state: RootState) => state.ticket.discountCode
export const selectDiscountName = (state: RootState) => state.ticket.discountName
export const selectDiscountCodeError = (state: RootState) => state.ticket.discountCodeError
export const selectDiscountCodeErrorCode = (state: RootState) => state.ticket.discountCodeErrorCode
export const selectDiscountCodeValid = (state: RootState) => state.ticket.discountCodeValid
export const selectDiscountCodeLoading = (state: RootState) => state.ticket.discountCodeLoading

// --- S5-04: Gift card selectors ---
export const selectGiftCardNumber = (state: RootState) => state.ticket.giftCardNumber
export const selectGiftCardBalance = (state: RootState) => state.ticket.giftCardBalance
export const selectGiftCardAppliedAmount = (state: RootState) => state.ticket.giftCardAppliedAmount
export const selectGiftCardErrors = (state: RootState) => state.ticket.giftCardErrors

// --- S5-04: Payment tender selector ---
export const selectTenderTypeGuid = (state: RootState) => state.ticket.tenderTypeGuid

// --- STO-3425: DSP delivery ETA selector ---
export const selectDistanceLeadTimeInMinutes = (state: RootState) =>
	state.ticket.distanceLeadTimeInMinutes

// --- STO-3489: DSP preferred quote ID selector ---
export const selectPreferredQuoteId = (state: RootState) => state.ticket.preferredQuoteId

// --- S5-04: Derived selectors ---

// Raw grand total from the server calculation — 0 until calculate() has run successfully.
export const selectGrandTotal = (state: RootState) =>
	state.ticket.calculations?.totals.grandTotal ?? 0

// True when the applied gift card amount covers the entire order total.
// Effect: payment method radio buttons become disabled, tip section is hidden.
// Ember: order-checkout.js isFullyPayByGiftCard (lines 122–124).
export const selectIsFullyPayByGiftCard = (state: RootState): boolean => {
	const applied = state.ticket.giftCardAppliedAmount
	const total = state.ticket.calculations?.totals.grandTotal ?? 0
	return applied !== null && total > 0 && applied >= total
}

// Tip in cents, respecting the isFullyPayByGiftCard gate.
// Ember: ticket.js tipAmount computed (lines 156–167).
export const selectTipAmountInCents = (state: RootState): number => {
	if (selectIsFullyPayByGiftCard(state)) return 0
	const { selectedTip, tipPercentage, tipCustomAmount, calculations, tipSettings } = state.ticket
	if (selectedTip === 'percent' && tipPercentage !== null) {
		// Ember: ticket.js:148-151 — Object.entries(calculations.tips).sort().map(e => e[1])
		// Server returns tipAmount1/2/3 keys. Sort alphabetically (matching Ember) to get
		// an ordered array [cents0, cents1, cents2], then find the position of the selected
		// percentage within the location's configured presets to get the right index.
		if (calculations?.tips) {
			const sortedCents = Object.entries(calculations.tips)
				.sort(([a], [b]) => a.localeCompare(b))
				.map(([, v]) => v)
			if (tipSettings !== null) {
				const presets = [
					tipSettings.predefinedTipPercentage1,
					tipSettings.predefinedTipPercentage2,
					tipSettings.predefinedTipPercentage3,
				]
					.filter((p) => p > 0)
					.map((p) => Math.round(p / TIP_PERCENTAGE_DIVISOR))
				const idx = presets.indexOf(tipPercentage)
				if (idx !== -1 && sortedCents[idx] !== undefined) {
					return sortedCents[idx]
				}
			}
		}
		// Fallback: server tips not yet available — calculate client-side.
		// Use subtotal when includeTaxInTipSuggestion is false, grandTotal when true.
		// Ember: ticket.js:154 — Math.round(this.total * percentage / 1000000)
		const { grandTotal, subtotal } = calculations?.totals ?? {}
		const base =
			tipSettings?.includeTaxInTipSuggestion === false
				? (subtotal ?? grandTotal ?? 0)
				: (grandTotal ?? subtotal ?? 0)
		return Math.round((base * tipPercentage) / 100)
	}
	if (selectedTip === 'custom' && tipCustomAmount !== null) {
		return Math.round(tipCustomAmount * 100)
	}
	return 0
}

// Amount still owed after any gift card redemption, floored at 0.
// Ember: ticket.js dueAmount computed (lines 171–176).
export const selectDueAmount = (state: RootState): number => {
	const grandTotal = state.ticket.calculations?.totals.grandTotal ?? 0
	const tipCents = selectTipAmountInCents(state)
	const giftApplied = state.ticket.giftCardAppliedAmount ?? 0
	return Math.max(0, grandTotal + tipCents - giftApplied)
}

// Coupon discount amount for cart breakdown display.
// Reads directly from calculations.discounts[] filtered by source === 'coupon'.
// This is the correct approach — the server returns the coupon amount per entry.
// The previous totals-diff formula overstated the coupon when loyalty was also active
// because grandTotal was already reduced by both discounts combined.
// Ember: ticket.js — uses discount.amount from the discounts array entry.
export const selectCouponDiscountAmount = (state: RootState): number => {
	if (!state.ticket.discountCodeValid) return 0
	const couponEntry = state.ticket.calculations?.discounts?.find((d) => d.source === 'coupon')
	return couponEntry?.amount ?? 0
}

// Tips are disabled when the selected tender does not allow them.
// Driven by isTipDisabledForSelectedTender, which is set by setTenderTypeGuid when the
// user selects a payment method in CheckoutPage (dispatched via onPaymentMethodChange).
// Ember: ticket.js isTipsDisabled computed (lines 144–146).
export const selectIsTipsDisabled = (state: RootState): boolean =>
	state.ticket.isTipDisabledForSelectedTender

// True when dueAmount is zero AND no gift card has been applied AND not a DSP order.
// Triggers auto-selection of the CASH tender (e.g. fully discounted coupon, free item).
// Ember: order-checkout.js isCustomCashPayment (lines 215–220).
// DSP excluded: a DSP order with dueAmount=0 is handled by isDspPaymentRequired in CheckoutPage,
// not by the cash auto-select path. Matches Ember's explicit !isServedByDDDOrDSP() guard.
export const selectIsCustomCashPayment = (state: RootState): boolean => {
	// STO-3749: calculations null means the server response hasn't arrived yet (e.g. page
	// refresh). dueAmount falls back to 0 when grandTotal is absent, which would make this
	// selector return true and auto-select CASH — poisoning tenderRef for wallet payments.
	// Only evaluate when calculations is present so we are acting on a real server total.
	if (!state.ticket.calculations) return false
	const dueAmount = selectDueAmount(state)
	const giftApplied = state.ticket.giftCardAppliedAmount
	const isServedByDSP =
		state.ticket.selectedOrderType?.deliveryServedWithDSP === true ||
		state.ticket.selectedOrderType?.isServedByDoorDashDrive === true
	return dueAmount === 0 && giftApplied === null && !isServedByDSP
}

// Whether the tip selector is interactive. False when any of the three conditions hold:
// - selected tender disallows tips (e.g. "On Receipt"/cash)
// - gift card fully covers the order (no payment needed)
// - order total is zero / custom cash payment
// When false the section stays mounted but buttons are disabled/greyed out (STO-3795).
// Ember: order-checkout.js shouldShowTipsView (lines 81–93).
export const selectTipsInteractive = (state: RootState): boolean =>
	!selectIsTipsDisabled(state) &&
	!selectIsFullyPayByGiftCard(state) &&
	!selectIsCustomCashPayment(state)

// The lesser of the gift card balance and the order grand total.
// Ensures the user cannot redeem more than the order is worth.
// Ember: order-checkout.js applicableGiftCardBalance (lines 125–127).
export const selectApplicableGiftCardBalance = (state: RootState): number => {
	const balance = state.ticket.giftCardBalance ?? 0
	const grandTotal = state.ticket.calculations?.totals.grandTotal ?? 0
	return Math.min(balance, grandTotal)
}

// Ember parity: menu-schedule.js isAvailable + checkout-button.js hasNoOffHoursRestrictions.
//
// Two distinct checks combined:
// 1. Schedule hours check: for ASAP orders, now must fall within at least one schedule hour
//    window. Dine-in and later (deferred) orders skip this check entirely — dine-in because the
//    table is always accessible (Ember: dineIn || hours.any(h => h.isAvailable)), deferred because
//    the availability/schedules API already validated the time and bootstrap hours cannot describe
//    a future window (see the note on the isDeferred branch below).
// 2. Off-hours restriction: when dine-in AND the schedule is marked duringOffHours AND the
//    venue has NOT enabled dineInAllowDuringOffHours, checkout is blocked.
//    (Ember: !dineIn || !duringOffHours || dineInAllowDuringOffHours)
//
// When no schedule is selected the store is treated as available — order settings
// would have blocked upstream if no valid schedule existed.
export const selectIsScheduleAvailable = createSelector(
	(state: { ticket: TicketState }) => state.ticket.selectedMenuSchedule,
	(state: { ticket: TicketState }) => state.ticket.selectedOrderTypeGuid,
	(state: { ticket: TicketState }) => state.ticket.dineInAllowDuringOffHours,
	(state: { ticket: TicketState }) => state.ticket.isDeferred,
	(state: { ticket: TicketState }) => state.ticket.expectedTimeUtc,
	(
		schedule,
		selectedOrderTypeGuid,
		dineInAllowDuringOffHours,
		isDeferred,
		expectedTimeUtc,
	): boolean => {
		const isDineIn = selectedOrderTypeGuid === 'DINE_IN'

		if (!schedule) return true // no schedule selected — treat as available

		// Off-hours restriction (Ember: hasNoOffHoursRestrictions)
		// Block dine-in checkout when the schedule is in off-hours and the venue disallows it.
		if (isDineIn && schedule.duringOffHours && !dineInAllowDuringOffHours) return false

		// Schedule hours check (Ember: menuSchedule.isAvailable)
		// Dine-in bypasses this — the table is always accessible regardless of schedule hours.
		if (isDineIn) return true

		// isDeferred without a time means the order is in an invalid transitional state — block checkout.
		if (isDeferred && !expectedTimeUtc) return false

		// Later orders perform no local hours validation — availability/schedules is the sole
		// authority, and MenuLayout redirects to Order Settings when it returns no schedules for
		// the selected time. Ember behaves identically but arrives there differently: for deferred
		// orders it swaps the bootstrap menu-schedule for an available-menu-schedule record whose
		// isAvailable is a hardcoded true. Ember could not validate locally even if it wanted to —
		// its menu-schedule serializer collapses hours to a single now-relevant window, so
		// menu-schedule.isAvailable only ever answers "is this schedule live right now".
		// See docs/STO-3817-ember-data-layer-analysis.md.
		if (isDeferred) return true

		// ASAP: the store must be inside one of the schedule's windows right now.
		const now = new Date()
		return schedule.hours.some(
			(hour) => now >= new Date(hour.startDateTime) && now < new Date(hour.endDateTime),
		)
	},
)

// Derived selector — computes whether the cart total meets the selected order type's
// min/max constraints. All amounts in cents. Returns null when no constraint applies.
// variant: 'below-min' | 'min-reached' | 'above-max'
export type OrderTypeAmountAlertVariant = 'below-min' | 'min-reached' | 'above-max'
export interface OrderTypeAmountAlert {
	variant: OrderTypeAmountAlertVariant
	orderTypeName: string
	amountCents: number // the min or max amount in cents
	differenceCents: number // how far below min or above max the cart currently is
}

export const selectOrderTypeAmountAlert = createSelector(
	(state: { ticket: TicketState }) => state.ticket.selectedOrderType,
	(state: { ticket: TicketState }) => state.ticket.calculations,
	(state: { ticket: TicketState }) => state.ticket.items,
	(selectedOrderType, calculations, items): OrderTypeAmountAlert | null => {
		if (!selectedOrderType || items.length === 0) return null
		if (!calculations?.totals) return null
		const total = calculations.totals.grandTotal
		const min = selectedOrderType.minOrderAmount
		const max = selectedOrderType.maxOrderAmount
		const name = selectedOrderType.name

		// Max check first — exceeding max always blocks, regardless of min state.
		if (max != null && total > max) {
			return {
				variant: 'above-max',
				orderTypeName: name,
				amountCents: max,
				differenceCents: total - max,
			}
		}

		if (min != null) {
			if (total < min) {
				return {
					variant: 'below-min',
					orderTypeName: name,
					amountCents: min,
					differenceCents: min - total,
				}
			}
			return {
				variant: 'min-reached',
				orderTypeName: name,
				amountCents: min,
				differenceCents: 0,
			}
		}

		return null
	},
)

export const selectLoyaltyDiscount = (state: { ticket: TicketState }) =>
	state.ticket.loyaltyDiscount

export const selectLoyaltyDiscountError = (state: { ticket: TicketState }) =>
	state.ticket.loyaltyDiscountError

// Loyalty discount name and amount for cart breakdown display.
// V1 parity: order-details.hbs iterates calculations.discounts and shows name + neg(amount) for each.
// V2 handles coupon separately (totals-diff); these selectors cover the loyalty case only.
const selectLoyaltyDiscountEntry = (state: { ticket: TicketState }) =>
	state.ticket.calculations?.discounts?.find((d) => d.source === 'loyalty') ?? null

export const selectLoyaltyDiscountName = (state: { ticket: TicketState }): string | null =>
	selectLoyaltyDiscountEntry(state)?.name ?? null

export const selectLoyaltyDiscountAmount = (state: { ticket: TicketState }): number | null => {
	const entry = selectLoyaltyDiscountEntry(state)
	return entry?.amount !== undefined ? entry.amount : null
}

export default ticketSlice.reducer
